"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PlusIcon, PencilIcon, Trash2Icon } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useColorRulesQuery, invalidateColorRulesQuery } from "@/lib/queries";
import type { ColorRule } from "@/prisma/generated/client";
import { deleteColorRule } from "@/app/lib/actions/color-rules";

import ColorRuleForm from "./color-rule-form";
import {
  dominantColorLabels,
  dominantNuanceLabels,
} from "./dominant-code-labels";

function AdministrationColorRules() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useColorRulesQuery({
    sort: { createdAt: "desc" },
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ColorRule | null>(null);

  const [deletingRule, setDeletingRule] = useState<ColorRule | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const refresh = () => invalidateColorRulesQuery(queryClient);

  const openCreate = () => {
    setEditingRule(null);
    setFormOpen(true);
  };

  const openEdit = (rule: ColorRule) => {
    setEditingRule(rule);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingRule) return;
    setDeleteLoading(true);
    await deleteColorRule({ id: deletingRule.id });
    setDeleteLoading(false);
    setDeletingRule(null);
    refresh();
  };

  const rules = data?.colorRules ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                Couleur / Nuance (AUDI & spécificités)
              </CardTitle>
              <p className="text-muted-foreground mt-1 text-sm">
                Historisation et contrôle de conflits (versionning actif).
              </p>
            </div>
            <Button size="sm" onClick={openCreate}>
              <PlusIcon className="mr-1.5 size-4" />
              Nouvelle règle
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isError && (
            <p className="px-6 py-4 text-sm text-red-600">
              Erreur lors du chargement des règles.
            </p>
          )}

          {isLoading ? (
            <div className="text-muted-foreground flex items-center justify-center py-12 text-sm">
              Chargement…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">Marque</TableHead>
                  <TableHead className="w-20">Année</TableHead>
                  <TableHead className="w-28">Exclusif</TableHead>
                  <TableHead className="w-28">Nuance</TableHead>
                  <TableHead className="w-28">Couleur</TableHead>
                  <TableHead>Code couleur</TableHead>
                  <TableHead>Libellé</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell className="font-mono">
                      {rule.brandLetter}
                    </TableCell>
                    <TableCell className="font-mono">{rule.year}</TableCell>
                    <TableCell>{rule.exclusiveProgram}</TableCell>
                    <TableCell>
                      {dominantNuanceLabels[rule.dominantNuanceCode as 0 | 1]}
                    </TableCell>
                    <TableCell>
                      {
                        dominantColorLabels[
                          rule.dominantColorCode as
                            | 0
                            | 1
                            | 2
                            | 3
                            | 4
                            | 5
                            | 6
                            | 7
                            | 8
                            | 9
                        ]
                      }
                    </TableCell>
                    <TableCell className="font-mono">
                      {rule.codeColor}
                    </TableCell>
                    <TableCell>{rule.labelColor}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-primary size-7"
                          onClick={() => openEdit(rule)}
                        >
                          <PencilIcon className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive size-7"
                          onClick={() => setDeletingRule(rule)}
                        >
                          <Trash2Icon className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-muted-foreground py-6 text-center"
                    >
                      Aucune règle active.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          )}

          {!isLoading && rules.length > 0 ? (
            <div className="text-muted-foreground border-t px-6 py-3 text-xs">
              {total} règles actives · Versionnement actif
            </div>
          ) : null}
        </CardContent>
      </Card>

      <ColorRuleForm
        key={editingRule?.id ?? "new"}
        rule={editingRule}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={refresh}
      />

      <AlertDialog
        open={!!deletingRule}
        onOpenChange={(o) => !o && setDeletingRule(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la règle ?</AlertDialogTitle>
            <AlertDialogDescription>
              La règle sera désactivée (soft delete) et historisée via le
              versionnement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export { AdministrationColorRules };
